import { defineStore } from "pinia";
import Router from './../routes';
import Http from '../utils/http';
import Utils from '../utils/utils';
import i18n from "../localizations";
import { useRootStore } from "./root";

export const useDashboardStore = defineStore("dashboard", {
    state: () => ({
        lastCreatedGame: null,
        lastInvitedGame: null,
        lecturer: null,
        loadingLecturer: false,
        lecturers: []
    }),

    actions: {
        setPlayerAsReady(playerId) {
            const rootStore = useRootStore();
            
            const players = rootStore.isPlayer ? this.lastInvitedGame.teamPlayers : this.lastCreatedGame.players;
            const foundPlayer = players.find(p => p.id === playerId);
            
            foundPlayer && (foundPlayer.ready = true);
        },
        
        updateGameState(newState) {
            const rootStore = useRootStore();
            const game = rootStore.isPlayer ? this.lastInvitedGame : this.lastCreatedGame;
         
            game.state = newState;
        },

        setLastCreatedGame(game) {
            if (Utils.isObjectEmpty(game)) {
                this.lastCreatedGame = null;
                return;
            }

            this.lastCreatedGame = {
                id: game['id'],
                templateKey: game['template_key'],
                situationName: game['situation_name'],
                players: game['players'].map(player => ({
                    id: player['id'],
                    userId: player['user_id'],
                    fullName: player['full_name'],
                    ready: player['ready']
                })),
                teamCount: game['team_count'],
                state: game['state']
            };
        },

        setLastInvitedGame(game) {
            if (Utils.isObjectEmpty(game)) {
                this.lastInvitedGame = null;
                return;
            }

            const teamPlayers = game['team_players'].map(player => ({
                id: player['id'],
                userId: player['user_id'],
                fullName: player['full_name'],
                divisions: player['divisions'].map(d => ({ id: d['id'], key: d['key']})),
                ready: player['ready']
            }));

            this.lastInvitedGame = {
                id: game['id'],
                situation: {
                    id: game['situation']['id'],
                    name: game['situation']['name']
                },
                teamPlayers: teamPlayers,
                ready: game['ready'],
                playerCount: game['player_count'],
                readyPlayerCount: game['ready_player_count'],
                state: game['state']
            }
        },

        setLecturer(lecturer) {
            if (lecturer === null) {
                this.lecturer = null;
                return;
            }

            this.lecturer = {
                id: lecturer['id'],
                fullName: lecturer['full_name'],
                avatar: lecturer['avatar']
            };
        },

        addLecturers(lecturers) {
            lecturers.forEach(lecturer => 
                this.lecturers.push({
                    id: lecturer['id'],
                    fullName: lecturer['full_name'],
                    email: lecturer['email'],
                    avatar: lecturer['avatar']
                })
            );
        },

        clearLecturers() {
            this.lecturers = [];
        },

        clearState() {
            this.$reset();
        },
        
        loadUserLecturer() {
            this.loadingLecturer = true;

            Http.get('/api/user-lecturer')
                .then(response => {
                    this.setLecturer(response.data);
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.loadingLecturer = false;
                });
        },

        loadLecturers() {
            Http.get('/api/roles/lecturer/users')
                .then(response => {
                    const lecturers = response.data || [];

                    this.clearLecturers();
                    this.addLecturers(lecturers);
                })
                .catch(error => console.error(error));
        },

        loadLastCreatedGame() {
            Http.get('/api/last-created-game')
                .then(response => {
                    this.setLastCreatedGame(response.data);
                })
                .catch(error => console.error(error))
        },

        loadLastInvitedGame() {
            Http.get('/api/last-invited-game')
                .then(response => {
                    this.setLastInvitedGame(response.data);
                })
                .catch(error => console.error(error));
        },

        startGame(gameId) {
            Http.post(`/api/games/${gameId}/start-game`)
                .then(_ => {})
                .catch(error => console.error(error));
        },

        async cancelGame(gameId) {
            return Http.post(`/api/games/${gameId}/cancel-game`)
                .then(_ => {})
                .catch(error => console.error(error));
        },
        
        async finishGame(gameId) {
            return Http.post(`/api/games/${gameId}/finish-game`)
                .then(_ => {})
                .catch(error => console.error(error));
        },

        acceptInvitedGame() {
            Http.put('/api/accept-invited-game')
                .catch(error => console.error(error));
        },

        changeLecturer(payload) {
            const rootStore = useRootStore();
            
            Http.put('/api/change-lecturer', payload)
                .then(response => {
                    const lecturer = response.data;
                    const notification = Utils.createNotification(
                        'success',
                        i18n.t('notifications.lecturer_successfully_changed', { lecturerName: lecturer["full_name"] })
                    );
                    
                    rootStore.updateUserLecturer(lecturer.id);
                    rootStore.clearNotifications();
                    rootStore.pushNotification(notification);

                    Router.push({ name: 'dashboard' }, null);
                })
                .catch(error => console.error(error))
        },

        loadUsersWithoutRole() {
            return new Promise((resolve, reject) => {
                Http.get('/api/users/no-roles')
                    .then(response => resolve(response.data))
                    .catch(error => reject(error));
            })
        }
    }
})